// ---------------------------------------------------------------------------
// Estratto VERBATIM da TabPreventivi.tsx (Task #3874 — split interno Fase 2).
// Nessuna modifica logica: JSX spostato tale e quale, stato e handler restano
// nel componente padre e arrivano via props esplicite.
// ---------------------------------------------------------------------------
import type { Dispatch, SetStateAction } from 'react';
import {
  Clock, Calendar, Plus, Trash2, CheckCircle, AlertTriangle, Loader2, CreditCard,
} from 'lucide-react';
import type { PaymentSetupState } from '../types';
import { ModalWrapper } from '../shared';

type PsInstallment = { number: number; amount: number; due_date: string; description: string };
type PsDateAdjustPending = { index: number; oldDate: string; newDate: string; deltaDays: number } | null;
type PsFrequency = 'monthly' | 'quarterly' | 'semiannual' | 'annual';

interface PaymentSetupModalProps {
  paymentSetupModal: PaymentSetupState | null;
  setPaymentSetupModal: Dispatch<SetStateAction<PaymentSetupState | null>>;
  setPsDateAdjustPending: Dispatch<SetStateAction<PsDateAdjustPending>>;
  psQuoteTotal: number;
  psDuration: number;
  psOnDurationChange: (months: number) => void;
  psInstallmentPlan: PsInstallment[];
  psStartDate: string;
  psOnStartDateChange: (date: string) => void;
  psEndDate: string;
  psFrequency: PsFrequency;
  psOnFrequencyChange: (freq: PsFrequency) => void;
  psAddInstallment: () => void;
  psUpdateInstallment: (index: number, field: string, value: string | number) => void;
  psRemoveInstallment: (index: number) => void;
  psDateAdjustPending: PsDateAdjustPending;
  psApplyDateAdjustment: (adjustSubsequent: boolean) => void;
  psPlanValid: boolean;
  psPlanTotal: number;
  psChargeFirstImmediately: boolean;
  setPsChargeFirstImmediately: Dispatch<SetStateAction<boolean>>;
  psSending: boolean;
  handleSendPaymentSetup: () => Promise<void>;
}

export function PaymentSetupModal({
  paymentSetupModal, setPaymentSetupModal, setPsDateAdjustPending,
  psQuoteTotal, psDuration, psOnDurationChange, psInstallmentPlan,
  psStartDate, psOnStartDateChange, psEndDate, psFrequency, psOnFrequencyChange,
  psAddInstallment, psUpdateInstallment, psRemoveInstallment,
  psDateAdjustPending, psApplyDateAdjustment, psPlanValid, psPlanTotal,
  psChargeFirstImmediately, setPsChargeFirstImmediately, psSending, handleSendPaymentSetup,
}: PaymentSetupModalProps) {
  return (
      <ModalWrapper open={!!paymentSetupModal} onClose={() => { setPaymentSetupModal(null); setPsDateAdjustPending(null); }} title="Configura Piano Rateale e Invia Setup Pagamento" wide>
        {paymentSetupModal && (
          <div className="space-y-5">
            <div className="bg-muted dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-foreground dark:text-white font-medium">Preventivo {paymentSetupModal.quote_number}</h4>
                <span className="text-foreground dark:text-white font-bold">Totale: € {psQuoteTotal.toFixed(2)}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div><span className="text-muted-foreground dark:text-muted-foreground/70">Cliente:</span> <span className="text-foreground dark:text-white">{paymentSetupModal.tenant_name || paymentSetupModal.contact_first_name || '-'}</span></div>
                <div><span className="text-muted-foreground dark:text-muted-foreground/70">Netto:</span> <span className="text-foreground dark:text-white">€ {Number(paymentSetupModal.subtotal_net ?? 0).toFixed(2)}</span></div>
                <div><span className="text-muted-foreground dark:text-muted-foreground/70">IVA:</span> <span className="text-foreground dark:text-white">€ {Number(paymentSetupModal.total_vat ?? 0).toFixed(2)}</span></div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-foreground dark:text-white font-medium flex items-center gap-2 mb-3"><Clock className="h-4 w-4" /> Durata Contrattuale / Rate</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-muted-foreground dark:text-muted-foreground/70 text-xs mb-1">Durata / Rateo</label>
                  <select value={psDuration} onChange={(e) => psOnDurationChange(parseInt(e.target.value))}
                    className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
                    <option value={0}>Una Tantum</option>
                    <option value={1}>1 Mese</option>
                    <option value={3}>3 Mesi</option>
                    <option value={6}>6 Mesi</option>
                    <option value={12}>12 Mesi (1 anno)</option>
                    <option value={18}>18 Mesi</option>
                    <option value={24}>24 Mesi (2 anni)</option>
                    <option value={36}>36 Mesi (3 anni)</option>
                    <option value={48}>48 Mesi (4 anni)</option>
                    <option value={60}>60 Mesi (5 anni)</option>
                  </select>
                  <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs mt-1.5">
                    {psDuration === 0
                      ? `1 fattura unica di € ${psQuoteTotal.toFixed(2)}`
                      : `${psInstallmentPlan.length} fatture da € ${psInstallmentPlan.length > 0 ? (psQuoteTotal / psInstallmentPlan.length).toFixed(2) : '0.00'} ciascuna`}
                  </p>
                </div>
                <div>
                  <label className="block text-muted-foreground dark:text-muted-foreground/70 text-xs mb-1">Decorrenza</label>
                  <input type="date" value={psStartDate} onChange={(e) => psOnStartDateChange(e.target.value)}
                    className="w-full bg-white dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm" />
                </div>
                {psDuration > 0 && (
                  <div>
                    <label className="block text-muted-foreground dark:text-muted-foreground/70 text-xs mb-1">Scadenza</label>
                    <input type="date" value={psEndDate} readOnly
                      className="w-full bg-muted dark:bg-gray-600 border border-border dark:border-gray-600 text-muted-foreground dark:text-muted-foreground/70 rounded-lg px-3 py-2 text-sm cursor-not-allowed" />
                  </div>
                )}
              </div>
            </div>

            {psDuration > 0 && (
            <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-foreground dark:text-white font-medium flex items-center gap-2"><Calendar className="h-4 w-4" /> Piano Rateale</h4>
                <div className="flex items-center gap-3">
                  <select value={psFrequency} onChange={(e) => psOnFrequencyChange(e.target.value as 'monthly' | 'quarterly' | 'semiannual' | 'annual')}
                    className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1 text-xs">
                    <option value="monthly">Mensile</option>
                    <option value="quarterly">Trimestrale</option>
                    <option value="semiannual">Semestrale</option>
                    <option value="annual">Annuale</option>
                  </select>
                  <button onClick={psAddInstallment} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"><Plus className="h-3 w-3" /> Aggiungi Rata</button>
                </div>
              </div>
              <div className="text-muted-foreground dark:text-muted-foreground/70 text-xs mb-2">
                {psInstallmentPlan.length} rate — Modifica un importo per ridistribuire le altre rate automaticamente
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {psInstallmentPlan.map((inst, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm w-16 text-center font-mono">#{inst.number}</span>
                    <input type="date" value={inst.due_date}
                      onChange={(e) => psUpdateInstallment(i, 'due_date', e.target.value)}
                      className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm flex-1" />
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">€</span>
                      <input type="number" step="0.01" value={inst.amount}
                        onChange={(e) => psUpdateInstallment(i, 'amount', e.target.value)}
                        className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm w-28" />
                    </div>
                    <input type="text" value={inst.description} placeholder="Note..."
                      onChange={(e) => psUpdateInstallment(i, 'description', e.target.value)}
                      className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-2 py-1.5 text-sm flex-1"
                      />
                    {psInstallmentPlan.length > 1 && (
                      <button onClick={() => psRemoveInstallment(i)} className="text-muted-foreground/70 hover:text-red-400 p-1"><Trash2 className="h-3 w-3" /></button>
                    )}
                  </div>
                ))}
              </div>
              {psDateAdjustPending && (
                <div className="mt-3 bg-blue-900/30 border border-blue-600 rounded-lg p-3">
                  <p className="text-blue-300 text-sm mb-2">
                    Hai spostato la rata #{psInstallmentPlan[psDateAdjustPending.index]?.number} di <strong>{Math.abs(psDateAdjustPending.deltaDays)} giorni</strong> {psDateAdjustPending.deltaDays > 0 ? 'in avanti' : 'indietro'}.
                    Vuoi adattare anche le date delle rate successive?
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => psApplyDateAdjustment(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">
                      Sì, adatta tutte
                    </button>
                    <button onClick={() => psApplyDateAdjustment(false)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm">
                      No, solo questa
                    </button>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border dark:border-gray-700">
                <span className="text-muted-foreground dark:text-muted-foreground/70 text-sm">{psInstallmentPlan.length} rate</span>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${psPlanValid ? 'text-green-400' : 'text-red-400'}`}>
                    Totale: € {psPlanTotal.toFixed(2)} / € {psQuoteTotal.toFixed(2)}
                  </span>
                  {psPlanValid && <CheckCircle className="h-4 w-4 text-green-400" />}
                  {!psPlanValid && psInstallmentPlan.length > 0 && <AlertTriangle className="h-4 w-4 text-red-400" />}
                </div>
              </div>
            </div>
            )}

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={psChargeFirstImmediately} onChange={(e) => setPsChargeFirstImmediately(e.target.checked)}
                      className="w-4 h-4 rounded border-border text-blue-600 focus:ring-blue-500" />
                    <div>
                      <span className="text-foreground dark:text-white font-medium text-sm">
                        {psDuration === 0 ? 'Addebita subito al completamento firma' : 'Addebita prima rata subito dopo la firma'}
                      </span>
                      <p className="text-muted-foreground dark:text-muted-foreground/70 text-xs mt-0.5">
                        {psChargeFirstImmediately
                          ? psDuration === 0
                            ? `L'importo di € ${psQuoteTotal.toFixed(2)} verrà addebitato immediatamente dopo la firma del contratto`
                            : `La prima rata di € ${psInstallmentPlan[0]?.amount?.toFixed(2) || '0.00'} verrà addebitata immediatamente dopo la firma del contratto`
                          : psDuration === 0
                            ? `L'addebito avverrà alla prossima esecuzione del cron (ore 8:00)`
                            : `La prima rata verrà addebitata alla prossima esecuzione del cron (ore 8:00)`
                        }
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-green-800 dark:text-green-300 text-sm leading-relaxed">
                <strong>Nessun addebito verrà effettuato al momento.</strong> Il cliente riceverà un'email con il {psDuration === 0 ? 'dettaglio del pagamento unico' : 'piano rateale qui configurato'} e un link per inserire il suo metodo di pagamento (carta o SEPA).
                Dopo la configurazione, il contratto verrà generato automaticamente con tutti i dati precompilati e inviato per la firma OTP.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => { setPaymentSetupModal(null); setPsDateAdjustPending(null); }} className="px-4 py-2 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white">Annulla</button>
              <button onClick={handleSendPaymentSetup} disabled={psSending || !psPlanValid}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 flex items-center gap-2">
                {psSending ? <><Loader2 className="h-4 w-4 animate-spin" /> Invio...</> : <><CreditCard className="h-4 w-4" /> Invia Setup Pagamento</>}
              </button>
            </div>
          </div>
        )}
      </ModalWrapper>
  );
}
